/**
 * Phase 15.6Q — Beat Detail Page.
 *
 * Audio preview + tier buy buttons. Stripe Checkout opens in a new tab; on
 * return the page polls /api/beats/sale/status to flip the UI to "Paid".
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams, useNavigate, useSearchParams } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import api from "@/lib/api";
import SharedNavigation from "@/components/SharedNavigation";
import ShareToCommunityDialog from "@/components/ShareToCommunityDialog";
import { useShareVisibility } from "@/lib/useShareVisibility";
import GiftPicker from "@/components/GiftPicker";
import AddBeatToPlaylistDialog from "@/components/AddBeatToPlaylistDialog";
import { usePlayer } from "@/components/player/PlayerProvider";
import { useCurrentUser } from "@/lib/useCurrentUser";
import { getProfilePhotoUrl, optimizeImage } from "@/lib/mediaUrls";
import { getPublicShareUrl } from "@/lib/publicUrl";
import RecordInStudioButton from "@/components/studio/RecordInStudioButton";

const formatUSD = (n) =>
  n == null
    ? "—"
    : new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 2 }).format(Number(n) || 0);

const TIER_ACCENT = {
  "Free Preview": { ring: "ring-gray-700", badge: "bg-gray-500/20 text-gray-200" },
  "Basic Lease": { ring: "ring-emerald-500/40", badge: "bg-emerald-500/20 text-emerald-200" },
  "Premium Lease": { ring: "ring-purple-500/50", badge: "bg-purple-500/20 text-purple-200" },
  "Unlimited Lease": { ring: "ring-pink-500/50", badge: "bg-pink-500/20 text-pink-200" },
  "Exclusive Rights": { ring: "ring-amber-500/60", badge: "bg-amber-500/25 text-amber-200" },
};

const TierCard = ({ tier, onBuy, buying, disabled, exclusiveSold }) => {
  const accent = TIER_ACCENT[tier.label] || TIER_ACCENT["Basic Lease"];
  const isExclusive = tier.label === "Exclusive Rights";
  const free = tier.label === "Free Preview" || Number(tier.price_usd) === 0;
  const blocked = isExclusive && exclusiveSold;
  return (
    <Card
      className={`p-5 bg-gray-950/60 border-purple-500/20 ring-1 ${accent.ring} hover:ring-2 transition-all flex flex-col gap-3`}
      data-testid={`tier-card-${tier.label.replace(/\s+/g, "-").toLowerCase()}`}
    >
      <div className="flex items-center justify-between">
        <span className={`text-[10px] font-black uppercase tracking-wider px-2 py-1 rounded-full ${accent.badge}`}>
          {tier.label}
        </span>
        <span className="text-2xl font-black text-white">
          {free ? "Free" : formatUSD(tier.price_usd)}
        </span>
      </div>
      <p className="text-sm text-gray-300 leading-relaxed flex-1 min-h-[60px]">{tier.terms}</p>
      {free ? (
        <Button disabled className="w-full bg-gray-700 text-gray-300 cursor-default" data-testid={`tier-free-button`}>
          Stream above ↑
        </Button>
      ) : blocked ? (
        <Button disabled className="w-full bg-rose-900/30 text-rose-200/80 cursor-not-allowed" data-testid={`tier-sold-button`}>
          Exclusive Sold
        </Button>
      ) : (
        <Button
          onClick={() => onBuy(tier)}
          disabled={disabled || buying === tier.label}
          className={`w-full font-bold ${isExclusive ? "bg-gradient-to-r from-amber-500 to-orange-500 text-black hover:from-amber-400" : "bg-gradient-to-r from-purple-600 to-pink-600 text-white hover:from-purple-500"}`}
          data-testid={`tier-buy-${tier.label.replace(/\s+/g, "-").toLowerCase()}`}
        >
          {buying === tier.label ? "Opening checkout…" : isExclusive ? "Buy Exclusive Rights" : "License Now"}
        </Button>
      )}
    </Card>
  );
};

const BeatDetailPage = () => {
  const { beatId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { currentUser } = useCurrentUser();
  const player = usePlayer();
  const [beat, setBeat] = useState(null);
  const [loading, setLoading] = useState(true);
  const [buying, setBuying] = useState(null);
  const [receipt, setReceipt] = useState(null);
  const [pollingSession, setPollingSession] = useState(false);
  const [showShareDialog, setShowShareDialog] = useState(false);
  // Phase 19.0 — Engagement state (likes + comments)
  const [liking, setLiking] = useState(false);
  const [comments, setComments] = useState([]);
  const [loadingComments, setLoadingComments] = useState(false);
  const [commentText, setCommentText] = useState("");
  const [postingComment, setPostingComment] = useState(false);
  // Phase 19.9 — Repost + Add-to-Playlist
  const [reposted, setReposted] = useState(false);
  const [showAddToPlaylist, setShowAddToPlaylist] = useState(false);
  // Feb 2026 — CEO share-visibility gate. Hides the existing Beat Share
  // to Community + Copy public link actions when OFF (no backend OG).
  const { canShare } = useShareVisibility();
  const beatsShareOn = canShare("beats");

  // Phase 19.0 — Load comments once the beat resolves
  useEffect(() => {
    if (!beatId) return;
    let alive = true;
    setLoadingComments(true);
    api.get(`/beats/${beatId}/comments`)
      .then((r) => { if (alive) setComments(r.data?.comments || []); })
      .catch(() => { /* silent: comments are non-blocking */ })
      .finally(() => { if (alive) setLoadingComments(false); });
    return () => { alive = false; };
  }, [beatId]);

  const handleToggleLike = async () => {
    if (!currentUser) { toast.error("Sign in to like beats"); navigate("/login"); return; }
    if (liking || !beat) return;
    setLiking(true);
    // Optimistic toggle
    const wasLiked = !!beat.is_liked_by_me;
    setBeat((b) => b ? {
      ...b,
      is_liked_by_me: !wasLiked,
      like_count: Math.max(0, (b.like_count || 0) + (wasLiked ? -1 : 1)),
    } : b);
    try {
      const r = await api.post(`/beats/${beatId}/like`);
      setBeat((b) => b ? { ...b, is_liked_by_me: !!r.data?.liked, like_count: r.data?.like_count ?? b.like_count } : b);
    } catch (e) {
      // Roll back
      setBeat((b) => b ? {
        ...b,
        is_liked_by_me: wasLiked,
        like_count: Math.max(0, (b.like_count || 0) + (wasLiked ? 1 : -1)),
      } : b);
      toast.error(e.response?.data?.detail || "Could not like beat");
    } finally {
      setLiking(false);
    }
  };

  const handlePostComment = async (e) => {
    e?.preventDefault?.();
    if (!currentUser) { toast.error("Sign in to comment"); navigate("/login"); return; }
    const content = commentText.trim();
    if (!content) return;
    setPostingComment(true);
    try {
      const r = await api.post(`/beats/${beatId}/comments`, { content });
      setComments((cs) => [r.data, ...cs]);
      setCommentText("");
      setBeat((b) => b ? { ...b, comment_count: (b.comment_count || 0) + 1 } : b);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not post comment");
    } finally {
      setPostingComment(false);
    }
  };

  const handleDeleteComment = async (commentId) => {
    if (!window.confirm("Delete this comment?")) return;
    try {
      await api.delete(`/beats/${beatId}/comments/${commentId}`);
      setComments((cs) => cs.filter((c) => c.comment_id !== commentId));
      setBeat((b) => b ? { ...b, comment_count: Math.max(0, (b.comment_count || 0) - 1) } : b);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not delete");
    }
  };

  // Phase 19.0 — Free/paid beat downloads
  // iter_222 — Real download via streaming endpoint with
  // Content-Disposition: attachment. Works on iOS Safari where the
  // previous `<a download>` cross-origin approach silently failed.
  const handleDownload = async () => {
    try {
      // Preflight the POST /download endpoint — it re-authorizes and
      // returns 401/402/403 as JSON with a `detail` we can toast BEFORE
      // sending the user off to a streaming URL. This keeps error UX
      // clean and prevents the download_count from ticking on failures.
      await api.post(`/beats/${beatId}/download`, null, { params: { preflight: true } });
      const token = (typeof window !== "undefined" && window.localStorage)
        ? window.localStorage.getItem("session_token")
        : null;
      const base = api.defaults.baseURL || "";
      const qs = token ? `?token=${encodeURIComponent(token)}` : "";
      const url = `${base}/beats/${beatId}/download-file${qs}`;
      // Trigger the download via a hidden iframe. Content-Disposition
      // on our streaming endpoint forces the browser to save without
      // navigating the SPA away. If the upstream fails (404), the
      // iframe silently renders the JSON error inside itself and the
      // user stays on this page — the analytics side won't tick
      // because it only fires on first byte delivered.
      const iframe = document.createElement("iframe");
      iframe.style.display = "none";
      iframe.src = url;
      document.body.appendChild(iframe);
      setTimeout(() => { try { iframe.remove(); } catch (_e) { /* gone */ } }, 60000);
      toast.success("Download started");
      setBeat((b) => b ? { ...b, download_count: (b.download_count || 0) + 1 } : b);
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 401) {
        toast.error(detail || "Sign in to download this beat");
        navigate("/login");
      } else if (status === 402) {
        toast.error(detail || "Purchase a license to unlock download");
      } else if (status === 403) {
        toast.error(detail || "This beat is stream-only");
      } else if (status === 404) {
        toast.error(detail || "Beat file is unavailable");
      } else {
        toast.error(detail || "Couldn't download");
      }
    }
  };

  useEffect(() => {
    let alive = true;
    api.get(`/beats/${beatId}`)
      .then((r) => { if (alive) setBeat(r.data); })
      .catch(() => { if (alive) toast.error("Beat not found"); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [beatId]);

  // Phase 15.6Q — return-from-Stripe polling: when Stripe redirects back to
  // /beats/:id?beats_session=cs_…, we poll /beats/sale/status until it flips
  // to paid (max 8 polls @ 2s).
  const beatsSession = searchParams.get("beats_session");
  const beatsCanceled = searchParams.get("beats_canceled");
  useEffect(() => {
    const sessionId = beatsSession;
    if (beatsCanceled) {
      toast.info("Checkout canceled");
      const next = new URLSearchParams(searchParams);
      next.delete("beats_canceled");
      setSearchParams(next, { replace: true });
      return;
    }
    if (!sessionId) return;
    let attempt = 0;
    let cancelled = false;
    setPollingSession(true);

    const tick = async () => {
      attempt += 1;
      try {
        const r = await api.get(`/beats/sale/status/${sessionId}`);
        if (cancelled) return;
        if (r.data?.status === "paid") {
          setReceipt(r.data);
          setPollingSession(false);
          toast.success(`License confirmed — ${r.data.tier_label}`);
          // strip the query param from URL
          const next = new URLSearchParams(searchParams);
          next.delete("beats_session");
          setSearchParams(next, { replace: true });
          // refresh beat (Exclusive Rights may have hidden it)
          api.get(`/beats/${beatId}`).then((rr) => setBeat(rr.data)).catch(() => {});
          return;
        }
      } catch (_e) { /* keep polling */ }
      if (attempt >= 8) {
        setPollingSession(false);
        toast.info("Still processing — refresh in a minute or check your dashboard.");
        return;
      }
      setTimeout(tick, 2000);
    };
    tick();
    return () => { cancelled = true; };
    // Note: intentionally scoped to session-id / canceled changes only.
    // beatId / searchParams / setSearchParams identity are stable within
    // a mount, and we already read the fresh values via closure inside
    // tick(). Re-running on their identity churn would restart polling.
  }, [beatsSession, beatsCanceled, beatId, searchParams, setSearchParams]);

  const togglePlay = () => {
    if (!beat?.audio_url) return;
    const beatTrackId = `beat_${beat.beat_id}`;
    const isCurrent = player?.track?.trackId === beatTrackId;
    if (isCurrent) {
      player.toggle();
      return;
    }
    player.play({
      trackId: beatTrackId,
      beat_id: beat.beat_id,
      title: beat.title,
      artist_name: beat.producer?.artist_name || beat.producer?.name || "Producer",
      artist_id: beat.producer?.user_id || beat.user_id,
      cover_url: beat.cover_url,
      audio_url: beat.audio_url,
      kind: "beat",
    }, { sourceLabel: "🎹 Beat Detail" });
  };

  // Phase 19.9 — initial Repost state for this beat
  useEffect(() => {
    if (!beat?.beat_id || !currentUser) return;
    let alive = true;
    api.get(`/beats/${beat.beat_id}/repost`)
      .then((r) => { if (alive) setReposted(!!r.data?.reposted); })
      .catch(() => {});
    return () => { alive = false; };
  }, [beat?.beat_id, currentUser]);

  const handleRepost = async () => {
    if (!currentUser) { toast.error("Sign in to repost"); navigate("/login"); return; }
    try {
      const r = await api.post(`/beats/${beat.beat_id}/repost`);
      setReposted(!!r.data?.reposted);
      toast.success(r.data?.reposted ? "🔀 Added to RGM Shuffle" : "Repost removed");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not repost");
    }
  };

  const handleBuy = async (tier) => {
    if (!currentUser) {
      navigate(`/login?next=/beats/${beatId}`);
      return;
    }
    setBuying(tier.label);
    try {
      const r = await api.post(`/beats/${beatId}/checkout`, {
        tier_label: tier.label,
        origin_url: `${window.location.origin}/beats/${beatId}`,
      });
      const url = r.data?.url;
      if (!url) throw new Error("Checkout URL missing");
      window.location.href = url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Checkout failed");
      setBuying(null);
    }
  };

  const exclusiveSold = !!beat?.exclusive_sold_at;

  const sortedTiers = useMemo(() => {
    const order = ["Free Preview", "Basic Lease", "Premium Lease", "Unlimited Lease", "Exclusive Rights"];
    return (beat?.tiers || []).slice().sort((a, b) => order.indexOf(a.label) - order.indexOf(b.label));
  }, [beat]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#08080c] text-gray-400 flex items-center justify-center">
        <div className="inline-block animate-spin rounded-full h-10 w-10 border-t-2 border-purple-500" />
      </div>
    );
  }
  if (!beat) {
    return (
      <div className="min-h-screen bg-[#08080c] text-white">
        <SharedNavigation currentUser={currentUser} />
        <div className="max-w-3xl mx-auto pt-32 px-6 text-center">
          <p className="text-2xl font-bold">Beat not found</p>
          <Link to="/beats" className="text-purple-300 hover:text-pink-300 mt-4 inline-block">← Back to marketplace</Link>
        </div>
      </div>
    );
  }

  const producer = beat.producer || {};

  return (
    <div className="min-h-screen bg-[#08080c]" data-testid="beat-detail-page">
      <SharedNavigation currentUser={currentUser} />

      <main className="max-w-6xl mx-auto px-4 sm:px-6 pt-24 pb-12">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <button
            type="button"
            onClick={() => (window.history.length > 1 ? navigate(-1) : navigate("/beats"))}
            className="text-purple-300 hover:text-pink-300 text-sm"
            data-testid="beat-detail-back-btn"
          >
            ← Back
          </button>
          {currentUser?.user_id === beat.user_id && (
            <div className="flex gap-2">
              <Button
                size="sm"
                onClick={() => navigate(`/submit-beat/${beat.beat_id}`)}
                className="bg-purple-500/30 hover:bg-purple-500/50 border border-purple-400/50 text-purple-100 text-xs"
                data-testid="beat-detail-edit-btn"
              >
                ✏ Edit beat
              </Button>
              <Button
                size="sm"
                onClick={async () => {
                  if (!window.confirm(`Hide "${beat.title}" from the marketplace? Restore anytime from your dashboard.`)) return;
                  try {
                    await api.delete(`/beats/${beat.beat_id}`);
                    toast.success("Beat hidden");
                    navigate("/producer");
                  } catch (e) {
                    const d = e?.response?.data?.detail;
                    toast.error(typeof d === "string" ? d : "Couldn't hide beat");
                  }
                }}
                className="bg-rose-500/30 hover:bg-rose-500/50 border border-rose-400/50 text-rose-100 text-xs"
                data-testid="beat-detail-hide-btn"
              >
                🚫 Hide beat
              </Button>
            </div>
          )}
        </div>

        {receipt && (
          <Card className="mt-4 p-4 bg-emerald-500/10 border-emerald-500/30 text-emerald-100 flex items-start gap-3" data-testid="purchase-receipt">
            <div className="text-3xl">✅</div>
            <div className="flex-1">
              <p className="font-bold">License confirmed — {receipt.tier_label}</p>
              <p className="text-sm opacity-90 mt-1">{receipt.tier_terms}</p>
              <Link to="/producer" className="text-xs underline opacity-80 hover:opacity-100">View receipts in your dashboard →</Link>
            </div>
          </Card>
        )}

        {pollingSession && (
          <Card className="mt-4 p-3 bg-purple-500/10 border-purple-500/30 text-purple-100 text-sm" data-testid="purchase-polling">
            ⏳ Confirming your payment with Stripe…
          </Card>
        )}

        <div className="grid md:grid-cols-2 gap-8 mt-6">
          {/* Cover + audio */}
          <div>
            <div className="relative aspect-square rounded-2xl overflow-hidden border border-purple-500/30 bg-gradient-to-br from-purple-900/40 via-fuchsia-900/30 to-black">
              {beat.cover_url ? (
                <img src={optimizeImage(beat.cover_url, 'card')} alt={beat.title} className="w-full h-full object-cover"
                  onError={(e) => { e.target.src = "/rgm-logo.png"; }} />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-9xl opacity-50">🎹</div>
              )}
              <button
                type="button"
                onClick={togglePlay}
                className="absolute inset-0 flex items-center justify-center bg-black/30 hover:bg-black/40 transition-colors"
                data-testid="beat-play-button"
              >
                <span className="w-20 h-20 rounded-full bg-white text-black text-4xl font-bold flex items-center justify-center shadow-2xl hover:scale-105 transition-transform">
                  {(player?.track?.trackId === `beat_${beat.beat_id}` && player?.isPlaying) ? "❚❚" : "▶"}
                </span>
              </button>
            </div>
            {/* Phase 19.8 — audio is owned by the global PlayerProvider, no
                local <audio> element here; the GlobalMiniPlayer keeps the
                beat playing while the user navigates away. */}
          </div>

          {/* Details */}
          <div>
            <div className="flex flex-wrap items-center gap-2 text-[11px] uppercase tracking-widest text-purple-300">
              {beat.genre && <span className="px-2 py-0.5 rounded-full bg-purple-500/15 border border-purple-500/30">{beat.genre}</span>}
              {beat.mood && <span className="px-2 py-0.5 rounded-full bg-fuchsia-500/15 border border-fuchsia-500/30">{beat.mood}</span>}
              {beat.bpm && <span className="px-2 py-0.5 rounded-full bg-amber-500/15 border border-amber-500/30">{beat.bpm} BPM</span>}
              {beat.key && <span className="px-2 py-0.5 rounded-full bg-emerald-500/15 border border-emerald-500/30">Key {beat.key}</span>}
            </div>
            <h1 className="text-4xl sm:text-5xl font-black text-white tracking-tight mt-3 leading-none" data-testid="beat-title">
              {beat.title}
            </h1>
            <Link to={`/artist/${producer.user_id || ""}`} className="flex items-center gap-3 mt-4 group">
              {producer.profile_photo ? (
                <img src={getProfilePhotoUrl(producer)} alt={producer.artist_name} className="w-10 h-10 rounded-full object-cover border border-purple-500/40" />
              ) : (
                <span className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-600 to-pink-600 flex items-center justify-center text-white font-bold">
                  {(producer.artist_name || "?").charAt(0).toUpperCase()}
                </span>
              )}
              <div>
                <p className="text-white font-bold group-hover:text-pink-300 transition-colors">{producer.artist_name || producer.name || "Producer"}</p>
                <p className="text-xs text-gray-400">{beat.plays?.toLocaleString() || 0} plays</p>
              </div>
            </Link>

            {beat.tags?.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-4">
                {beat.tags.map((t) => (
                  <span key={t} className="px-2 py-0.5 text-[11px] rounded-full bg-white/5 border border-white/10 text-gray-300">#{t}</span>
                ))}
              </div>
            )}
            {/* Phase 19.0 — Beat engagement bar: likes + comments + download */}
            <div className="mt-5 flex flex-wrap items-center gap-3" data-testid="beat-engagement-bar">
              <Button
                type="button"
                onClick={handleToggleLike}
                disabled={liking}
                className={
                  beat.is_liked_by_me
                    ? "bg-pink-600 hover:bg-pink-500 text-white"
                    : "bg-white/5 hover:bg-white/10 text-white border border-white/15"
                }
                data-testid="beat-like-button"
              >
                {beat.is_liked_by_me ? "❤️" : "🤍"} {beat.like_count || 0}
              </Button>
              <a
                href="#beat-comments"
                className="px-3 py-2 text-sm rounded-md bg-white/5 hover:bg-white/10 text-white border border-white/15"
                data-testid="beat-comments-jump"
              >
                💬 {beat.comment_count || 0}
              </a>
              <RecordInStudioButton beat={beat} currentUser={currentUser} />
              {beat.download_mode === "free" && (
                <Button
                  type="button"
                  onClick={handleDownload}
                  className="bg-gradient-to-r from-emerald-500 to-teal-500 text-black font-bold"
                  data-testid="beat-free-download"
                >
                  ⬇ Download MP3 (Free)
                </Button>
              )}
              {beat.download_mode === "paid" && (
                <Button
                  type="button"
                  onClick={handleDownload}
                  variant="outline"
                  className="border-amber-500/40 text-amber-100 hover:bg-amber-500/10"
                  data-testid="beat-paid-download"
                  title="Available after purchase"
                >
                  ⬇ Download (License required)
                </Button>
              )}
              {/* Phase 19.4 — Beat Gifting: support the producer */}
              <GiftPicker
                targetType="beat"
                targetId={beat.beat_id}
                recipientId={beat.user_id}
                currentUserId={currentUser?.user_id}
                currentUserRole={currentUser?.role}
                onSent={() => {
                  setBeat((b) => b ? { ...b, gifts_received: (b.gifts_received || 0) + 1 } : b);
                }}
                data-testid="beat-gift-picker"
              />
              {/* Phase 19.9 — Repost + Add-to-Playlist */}
              <Button
                type="button"
                onClick={handleRepost}
                variant="outline"
                className={reposted
                  ? "border-emerald-400/50 text-emerald-200 hover:bg-emerald-500/10"
                  : "border-purple-500/40 text-white hover:bg-purple-500/10"}
                data-testid="beat-repost-btn"
              >
                🔁 {reposted ? "Reposted" : "Repost"}
              </Button>
              <Button
                type="button"
                onClick={() => {
                  if (!currentUser) { navigate("/login"); return; }
                  setShowAddToPlaylist(true);
                }}
                variant="outline"
                className="border-gray-700 text-white hover:bg-white/5"
                data-testid="beat-add-to-playlist-btn"
              >
                🎼 + Playlist
              </Button>
              <span className="text-xs text-gray-500">
                {(beat.plays || 0).toLocaleString()} plays
                {(beat.download_count || 0) > 0 && ` · ${(beat.download_count || 0).toLocaleString()} downloads`}
                {(beat.gifts_received || 0) > 0 && ` · 🎁 ${beat.gifts_received}`}
              </span>
            </div>
          </div>
        </div>

        {/* TIER CARDS — buy buttons */}
        <section className="mt-12" data-testid="tier-grid-section">
          <h2 className="text-2xl font-black text-white mb-2">Choose your license</h2>
          <p className="text-sm text-gray-400 mb-6">
            All prices in USD. RGM takes a 10% intro platform fee — producers keep the rest.
          </p>
          {sortedTiers.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {sortedTiers.map((t) => (
                <TierCard
                  key={t.label}
                  tier={t}
                  onBuy={handleBuy}
                  buying={buying}
                  disabled={buying != null}
                  exclusiveSold={exclusiveSold}
                />
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-sm italic">This producer hasn&apos;t priced this beat for purchase yet.</p>
          )}
        </section>

        {/* Producer contact channels (fallback if buyer wants to talk first) */}
        {(beat.contact_email || beat.contact_instagram || beat.contact_discord || beat.contact_website) && (
          <section className="mt-10" data-testid="contact-producer-section">
            <h3 className="text-lg font-bold text-white mb-2">Reach the producer</h3>
            <div className="flex flex-wrap gap-2">
              {beat.contact_email && <a href={`mailto:${beat.contact_email}`} className="px-3 py-1.5 rounded-full bg-white/5 border border-white/15 text-sm text-white hover:bg-white/10">✉️ Email</a>}
              {beat.contact_instagram && <a href={beat.contact_instagram.startsWith("@") ? `https://instagram.com/${beat.contact_instagram.slice(1)}` : beat.contact_instagram} target="_blank" rel="noreferrer" className="px-3 py-1.5 rounded-full bg-white/5 border border-white/15 text-sm text-white hover:bg-white/10">📸 Instagram</a>}
              {beat.contact_discord && <span className="px-3 py-1.5 rounded-full bg-white/5 border border-white/15 text-sm text-white">💬 {beat.contact_discord}</span>}
              {beat.contact_website && <a href={beat.contact_website.startsWith("http") ? beat.contact_website : `https://${beat.contact_website}`} target="_blank" rel="noreferrer" className="px-3 py-1.5 rounded-full bg-white/5 border border-white/15 text-sm text-white hover:bg-white/10">🌐 Website</a>}
            </div>
          </section>
        )}

        {/* Phase 19.0 — Comments section */}
        <section id="beat-comments" className="mt-12" data-testid="beat-comments-section">
          <h3 className="text-xl font-black text-white mb-4">💬 Comments ({beat.comment_count || 0})</h3>
          {currentUser ? (
            <form onSubmit={handlePostComment} className="flex flex-col sm:flex-row gap-2 mb-6" data-testid="beat-comment-form">
              <input
                type="text"
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
                placeholder="Drop a comment for the producer…"
                maxLength={2000}
                className="flex-1 px-3 py-2 rounded-md bg-black/40 border border-gray-700 text-white text-sm focus:border-purple-500 outline-none"
                data-testid="beat-comment-input"
              />
              <Button
                type="submit"
                disabled={postingComment || !commentText.trim()}
                className="bg-gradient-to-r from-purple-600 to-pink-600 text-white"
                data-testid="beat-comment-submit"
              >
                {postingComment ? "Posting…" : "Post"}
              </Button>
            </form>
          ) : (
            <p className="text-sm text-gray-400 mb-6">
              <Link to="/login" className="text-purple-300 underline">Sign in</Link> to leave a comment.
            </p>
          )}

          {loadingComments ? (
            <p className="text-sm text-gray-500 italic">Loading comments…</p>
          ) : comments.length === 0 ? (
            <p className="text-sm text-gray-500 italic" data-testid="beat-comments-empty">
              No comments yet. Be the first to support this producer.
            </p>
          ) : (
            <div className="space-y-3">
              {comments.map((c) => {
                const a = c.author || {};
                const canDelete = currentUser?.user_id === c.user_id || currentUser?.user_id === beat.user_id;
                return (
                  <div
                    key={c.comment_id}
                    className="flex items-start gap-3 p-3 rounded-lg bg-gray-900/40 border border-gray-800"
                    data-testid={`beat-comment-${c.comment_id}`}
                  >
                    <Link to={`/artist/${a.user_id || ''}`} className="flex-shrink-0">
                      <img
                        src={getProfilePhotoUrl(a)}
                        alt={a.artist_name || 'User'}
                        className="w-9 h-9 rounded-full object-cover"
                      />
                    </Link>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2 flex-wrap">
                        <Link
                          to={`/artist/${a.user_id || ''}`}
                          className="text-white text-sm font-medium hover:text-purple-300"
                        >
                          {a.artist_name || a.name || 'Artist'}
                        </Link>
                        <span className="text-[11px] text-gray-500">
                          {c.created_at ? new Date(c.created_at).toLocaleString() : ''}
                        </span>
                      </div>
                      <p className="text-gray-200 text-sm whitespace-pre-wrap break-words mt-1">{c.content}</p>
                      {canDelete && (
                        <button
                          type="button"
                          onClick={() => handleDeleteComment(c.comment_id)}
                          className="text-[11px] text-gray-500 hover:text-red-400 mt-1"
                          data-testid={`beat-comment-delete-${c.comment_id}`}
                        >
                          Delete
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>

        {beatsShareOn && (
          <div className="mt-12 flex flex-col items-center gap-2">
            {currentUser && (
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowShareDialog(true)}
                className="border-purple-500/40 text-purple-100 hover:bg-purple-500/15"
                data-testid="beat-share-to-community"
              >
                📣 Share to Community
              </Button>
            )}
            <button
              type="button"
              onClick={() => { navigator.clipboard.writeText(getPublicShareUrl(`/beats/${beat.beat_id}`)); toast.success("Beat link copied"); }}
              className="text-xs text-purple-300 hover:text-pink-300 underline"
              data-testid="copy-beat-link"
            >
              Copy public link
            </button>
          </div>
        )}
      </main>
      <ShareToCommunityDialog
        open={showShareDialog}
        onClose={() => setShowShareDialog(false)}
        beatId={beat.beat_id}
        beatTitle={beat.title}
      />
      <AddBeatToPlaylistDialog
        open={showAddToPlaylist}
        onClose={() => setShowAddToPlaylist(false)}
        beat={beat}
      />
    </div>
  );
};

export default BeatDetailPage;
